import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Trash2, Plus, Minus, ShoppingCart, MapPin, User, Phone } from "lucide-react";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import Navbar from "../components/common/Navbar";
import { EmptyState } from "../components/common/UI";
import toast from "react-hot-toast";

export default function CheckoutPage() {
  const { user, profile } = useAuth();
  const { cart, updateQuantity, removeItem, clearCart, totalPrice } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: profile?.name || "",
    phone: profile?.phone || "",
    address: "",
    notes: "",
  });

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.address) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (cart.items.length === 0) {
      toast.error("Your cart is empty");
      return;
    }

    setSubmitting(true);
    try {
      // Get delivery fee from restaurant
      const { data: restaurant } = await supabase
        .from("restaurants")
        .select("delivery_fee")
        .eq("id", cart.restaurantId)
        .single();
      const deliveryFee = restaurant?.delivery_fee || 0;

      // Create order
      const { data: order, error } = await supabase
        .from("orders")
        .insert({
          user_id: user.id,
          restaurant_id: cart.restaurantId,
          customer_name: form.name,
          customer_phone: form.phone,
          customer_address: form.address,
          notes: form.notes,
          total: totalPrice + deliveryFee,
          delivery_fee: deliveryFee,
          status: "pending",
        })
        .select()
        .single();

      if (error) throw error;

      // Create order items
      const orderItems = cart.items.map((item) => ({
        order_id: order.id,
        menu_item_id: item.id,
        menu_item_name: item.name,
        menu_item_price: item.price,
        quantity: item.quantity,
        subtotal: item.price * item.quantity,
      }));

      const { error: itemsError } = await supabase.from("order_items").insert(orderItems);
      if (itemsError) throw itemsError;

      clearCart();
      navigate("/order-success", { state: { order, restaurantName: cart.restaurantName } });
    } catch (err) {
      toast.error(err.message || "Failed to place order");
    } finally {
      setSubmitting(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <>
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <EmptyState
            icon="🛒"
            title="Your cart is empty"
            description="Add some delicious items from a restaurant to get started."
            action={<Link to="/restaurants" className="btn-primary">Browse Restaurants</Link>}
          />
        </div>
      </>
    );
  }


  return (
    <>
      <Helmet><title>Checkout — Khana Spot</title></Helmet>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 page-enter">
        <h1 className="font-display text-2xl font-bold text-gray-900 mb-6">Your Order</h1>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Cart items */}
          <div className="lg:col-span-3 space-y-4">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100">
                <h2 className="font-semibold text-gray-900">
                  🍽️ {cart.restaurantName}
                </h2>
              </div>
              <div className="divide-y divide-gray-50">
                {cart.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-4 px-5 py-4">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 text-sm truncate">{item.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">₨{item.price} each</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity - 1)}
                        className="w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
                      >
                        <Minus className="w-3.5 h-3.5 text-gray-600" />
                      </button>
                      <span className="font-bold text-sm w-5 text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.id, item.quantity + 1)}
                        className="w-7 h-7 rounded-full bg-orange-100 hover:bg-orange-200 text-orange-600 flex items-center justify-center transition-colors"
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="w-20 text-right">
                      <span className="font-bold text-gray-900 text-sm">₨{(item.price * item.quantity).toLocaleString()}</span>
                    </div>
                    <button
                      onClick={() => removeItem(item.id)}
                      className="text-gray-300 hover:text-red-400 transition-colors ml-1"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery info form */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h2 className="font-semibold text-gray-900 mb-4">📦 Delivery Details</h2>
              <form id="checkout-form" onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="Your name"
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                      Phone Number *
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        required
                        placeholder="03XX-XXXXXXX"
                        className="input-field pl-10"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Delivery Address *
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-3.5 w-4 h-4 text-gray-400" />
                    <textarea
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      required
                      rows={2}
                      placeholder="Street, area, city..."
                      className="input-field pl-10 resize-none"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1.5">
                    Order Notes (optional)
                  </label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleChange}
                    rows={2}
                    placeholder="Special instructions..."
                    className="input-field resize-none"
                  />
                </div>
              </form>
            </div>
          </div>

          {/* Order summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5 sticky top-24">
              <h2 className="font-semibold text-gray-900 mb-4">💳 Order Summary</h2>
              <div className="space-y-2 text-sm">
                {cart.items.map((item) => (
                  <div key={item.id} className="flex justify-between text-gray-600">
                    <span>{item.name} × {item.quantity}</span>
                    <span>₨{(item.price * item.quantity).toLocaleString()}</span>
                  </div>
                ))}
                <div className="pt-2 border-t border-gray-100 flex justify-between text-gray-600">
                  <span>Subtotal</span>
                  <span>₨{totalPrice.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-600">
                  <span>Delivery Fee</span>
                  <span className="text-green-600">Calculated at checkout</span>
                </div>
                <div className="pt-2 border-t border-gray-200 flex justify-between font-bold text-gray-900 text-base">
                  <span>Total</span>
                  <span className="text-orange-600">₨{totalPrice.toLocaleString()}+</span>
                </div>
              </div>

              <button
                type="submit"
                form="checkout-form"
                disabled={submitting}
                className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
              >
                {submitting ? (
                  <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Placing Order...</>
                ) : (
                  <><ShoppingCart className="w-4 h-4" /> Place Order</>
                )}
              </button>

              <p className="text-xs text-gray-400 text-center mt-3">
                By placing the order you agree to our terms of service.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
